import { IonButton, IonCard, IonCardContent, IonCol, IonGrid, IonIcon, IonRow } from '@ionic/react';
import { useIntl } from 'react-intl';
import './ConnectedControllerDetails.scss';
import { DqText } from 'src/components/dq-text';
import { Badge } from 'src/components/reusable-components/Badge';
import { link, chevronForward } from 'ionicons/icons';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { BundleMessages } from 'src/intl/BundleMessages';
import { bleActions } from 'src/redux/ble/actions';
import { useDispatch } from 'react-redux';

export interface ConnectedControllerDetailsProps {
  /**
   * The connected controller to display
   */
  physicalSerial: string;
  /**
   * The in progress job on the connected controller
   */
  liveJob?: string;
  /**
   * The number of jobs on the connected controller
   */
  jobCount: number;
  /**
   * The number of bundles on the connected controller
   */
  bundleCount: number;
  /**
   * Firmware text for the connected controller
   */
  firmware?: string;
  /**
   * The callback triggered when the card is clicked
   */
  onCardClick: () => void;
  /**
   * The callback triggered to update the firmware of the connected controller
   */
  onUpdateFirmware?: (e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
}

const ConnectedControllerDetails: React.FC<ConnectedControllerDetailsProps> = ({
  firmware,
  physicalSerial,
  liveJob,
  jobCount,
  bundleCount,
  onUpdateFirmware,
  onCardClick,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const formattedControllerLabel = formatMessage(ControllerMessages.controllerIdLabel, {
    physicalId: physicalSerial,
  });

  return (
    <IonCard onClick={onCardClick} className="connected-controller-card">
      <IonCardContent>
        <IonGrid>
          <IonRow className="connected-controller-info-row ion-margin-bottom">
            <IonCol>
              <Badge isActive icon={link} color="secondary" isLarge />
              <DqText className="ion-margin-start" variant="body-strong">
                {formattedControllerLabel}
              </DqText>
            </IonCol>
            <IonCol size="auto">
              <IonIcon color="primary" icon={chevronForward} />
            </IonCol>
          </IonRow>
          <IonRow className="connected-controller-info-row">
            <IonCol size="auto" sizeLg="1">
              <DqText color="dark" variant="body-small">
                {liveJob && formatMessage(JobMessages.jobLiveNow)}
              </DqText>
            </IonCol>
            <IonCol className="controller-data-column">
              <DqText variant="body-small">{liveJob}</DqText>
            </IonCol>
          </IonRow>
          <IonRow className="connected-controller-info-row">
            <IonCol size="auto" sizeLg="1">
              <DqText color="dark" variant="body-small">
                {formatMessage(ControllerMessages.firmware)}
              </DqText>
            </IonCol>
            <IonCol className="controller-data-column">
              <DqText variant="body-small">{firmware}</DqText>
            </IonCol>
          </IonRow>
          <IonRow className="connected-controller-info-row">
            <IonCol size="auto" sizeLg="1">
              <DqText color="dark" variant="body-small">
                {formatMessage(JobMessages.jobLogsTitle)}
              </DqText>
            </IonCol>
            <IonCol className="controller-data-column">
              <DqText variant="body-small">{jobCount}</DqText>
            </IonCol>
          </IonRow>
          <IonRow className="connected-controller-info-row">
            <IonCol size="auto" sizeLg="1">
              <DqText color="dark" variant="body-small">
                {formatMessage(BundleMessages.bundlesHeader)}
              </DqText>
            </IonCol>
            <IonCol className="controller-data-column">
              <DqText variant="body-small">{bundleCount}</DqText>
            </IonCol>
          </IonRow>

          <IonRow className="ion-margin-top">
            {onUpdateFirmware && (
              <IonCol>
                <IonButton expand="block" fill="outline" color="primary" onClick={onUpdateFirmware}>
                  {formatMessage(ControllerMessages.updateFirmware)}
                </IonButton>
              </IonCol>
            )}
            <IonCol>
              <IonButton
                expand="block"
                fill="outline"
                onClick={e => {
                  e.stopPropagation();
                  dispatch(bleActions.openConnectControllerModal());
                }}
              >
                {formatMessage(ControllerMessages.changeController)}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonCardContent>
    </IonCard>
  );
};

export default ConnectedControllerDetails;
